/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { createDefaultGridModel, ROW_DIRTY_CLASS } from '@gh/common-internal-shared';
import { getRouteInitData, queryFormEditable, State } from '@gh/common-shared';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildEditableCheckboxColumn,
  FocusedCell,
  GridDefinition,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
} from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  getJobCsDetailsItemId,
  isJobCsItemQuoteChange,
  JobCsDetailsItem,
} from '../../../resources/job-cs-details.model';
import { JobCsDetailsCommandService } from '../job-cs-details-command.service';
import {
  JobCsDetailsState, queryJobCsFocusedQcCell,
  queryJobCsQuoteChanges,
} from '../job-cs-details.state';

@Component({
  standalone: false,
             selector: 'gh-job-cs-details-qc-list',
             templateUrl: 'job-cs-details-qc-list.component.html',
             styles: [STYLES_FLEX_COLUMN_LAYOUT],
             changeDetection: ChangeDetectionStrategy.OnPush,
           })
export class JobCsDetailsQcListComponent implements OnInit {
  focusedCell$: Observable<Maybe<FocusedCell>>;
  quoteChanges$: Observable<JobCsDetailsItem[]>;
  quoteChangesCount$: Observable<number>;
  qcGridDefinition: GridDefinition;

  constructor(private route: ActivatedRoute,
              private state: State<JobCsDetailsState>,
              private commands: JobCsDetailsCommandService,
              private gridDefinitionFactory: GridDefinitionFactory) {

  }

  get editable(): boolean {
    return this.state.project(queryFormEditable);
  }

  ngOnInit(): void {
    this.qcGridDefinition = this.buildQcGridDefinition();
    this.focusedCell$ = this.state.select(queryJobCsFocusedQcCell);
    this.quoteChanges$ = this.state.select(queryJobCsQuoteChanges);
    this.quoteChangesCount$ = this.quoteChanges$.pipe(map$((qcs) => qcs.length));

  }

  onAdd(): void {
    this.commands.itemAdd();
  }

  onRemove(item: JobCsDetailsItem): void {
    this.commands.itemRemove(item);
  }

  onMoveToItems(qc: JobCsDetailsItem): void {
    this.commands.moveToItems(qc);
  }

  isQuoteChange(item: JobCsDetailsItem): boolean {
    return isJobCsItemQuoteChange(item);
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  private buildQcGridDefinition(): LayoutBasedGridDefinition {
    const {qcGridLayout} = getRouteInitData(this.route);

    const model = createDefaultGridModel({
      editable: this.state.project(queryFormEditable),
      getId: getJobCsDetailsItemId,
      command: (entity, name, value) => this.commands.setQuoteChangeField(getJobCsDetailsItemId(entity), name, value),
    });

    return this.gridDefinitionFactory.buildLayoutBasedWithModel({
      options: <any>{
        rowSelection: { mode: 'singleRow', enableClickSelection: true, checkboxes: false },
        defaultColDef: {
          sortable: false,
        },
        rowClassRules: { [ROW_DIRTY_CLASS]: ({ data }: { data: JobCsDetailsItem }) => data.isDirty },
      },
      columns: {
        isHidden: buildEditableCheckboxColumn(model.getCellAccessor('isHidden')),
      },
      model,
      layout: qcGridLayout,
    });
  }
}
